import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authoritiesAPI } from '../../api/client';
import { AGENCY_TYPES, JURISDICTION_LEVELS, RANKS } from '../../lib/emergencyConstants';
import { useAuth } from '../../context/AuthContext';
import { Plus, Search, X, Phone, Mail, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';

function AuthCard({ auth, onView, onEdit, canEdit }) {
  const at = AGENCY_TYPES[auth.dependency_name] || AGENCY_TYPES.proteccion_civil;
  const jl = JURISDICTION_LEVELS[auth.jurisdiction_level] || { label: auth.jurisdiction_level };
  return (
    <div className={`soem-card hover:shadow-md transition-shadow overflow-hidden`} data-testid={`auth-card-${auth.id}`}>
      <div className={`p-5 ${at.bg} border-b ${at.border}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{at.emoji}</span>
            <div>
              <p className={`text-xs font-mono ${at.text}`}>{auth.authority_code}</p>
              <h3 className="font-bold text-soem-navy text-base leading-tight mt-0.5">{at.label}</h3>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span className="bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full font-semibold border">{jl.label}</span>
            {auth.rank && <span className="bg-white border text-slate-600 text-xs px-2 py-0.5 rounded-full font-medium">{RANKS[auth.rank]?.label || auth.rank}</span>}
          </div>
        </div>
      </div>
      <div className="p-4 space-y-2">
        {auth.titular_name && <p className="font-semibold text-soem-navy text-sm">{auth.titular_name}</p>}
        {auth.direct_phone && (
          <p className="text-xs text-slate-600 flex items-center gap-1.5"><Phone size={11} className="text-slate-400" /> {auth.direct_phone}</p>
        )}
        {auth.institutional_email && (
          <p className="text-xs text-slate-500 flex items-center gap-1.5 truncate"><Mail size={11} className="text-slate-400" /> {auth.institutional_email}</p>
        )}
      </div>
      <div className="border-t border-slate-100 px-4 py-3 flex gap-2">
        <button onClick={() => onView(auth.id)} className="soem-btn-primary flex-1 py-1.5 text-xs">Ver</button>
        {canEdit && <button onClick={() => onEdit(auth.id)} className="soem-btn-secondary px-3 py-1.5 text-xs">Editar</button>}
      </div>
    </div>
  );
}

export default function AuthoritiesList() {
  const navigate = useNavigate();
  const { isAdmin, isLider } = useAuth();
  const canEdit = isAdmin || isLider;

  const [authorities, setAuthorities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: '', dependency_name: '', jurisdiction_level: '' });
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('authorities-view') || 'grid');

  const setView = (v) => {
    setViewMode(v);
    localStorage.setItem('authorities-view', v);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.dependency_name) params.dependency_name = filters.dependency_name;
      if (filters.jurisdiction_level) params.jurisdiction_level = filters.jurisdiction_level;
      const [authRes, statsRes] = await Promise.all([authoritiesAPI.list(params), authoritiesAPI.stats()]);
      setAuthorities(authRes.data.items || []);
      setStats(statsRes.data);
    } catch { toast.error('Error al cargar autoridades'); }
    finally { setLoading(false); }
  }, [filters.dependency_name, filters.jurisdiction_level]);

  useEffect(() => { load(); }, [load]);

  const filtered = filters.q
    ? authorities.filter(a => (a.titular_name || '').toLowerCase().includes(filters.q.toLowerCase()) || AGENCY_TYPES[a.dependency_name]?.label.toLowerCase().includes(filters.q.toLowerCase()))
    : authorities;

  const hasFilters = filters.q || filters.dependency_name || filters.jurisdiction_level;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="authorities-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Autoridades Vinculadas</h1>
          <p className="text-slate-500 text-sm mt-1">{authorities.length} autoridades registradas</p>
        </div>
        {canEdit && (
          <button onClick={() => navigate('/dashboard/authorities/new')}
            className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="new-auth-btn">
            <Plus size={16} /> Nueva Autoridad
          </button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: 'proteccion_civil', label: 'Protección Civil', color: '#D97706' },
            { key: 'guardia_nacional', label: 'Guardia Nacional', color: '#0D1B2A' },
            { key: 'secretaria_salud', label: 'Sec. de la Salud', color: '#15803D' },
          ].map(s => {
            const at = AGENCY_TYPES[s.key];
            return (
              <div key={s.key} className="soem-card p-5 border-t-4" style={{ borderTopColor: s.color }}>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{at.emoji}</span>
                  <div>
                    <p className="soem-label">{s.label}</p>
                    <p className="text-3xl font-black text-soem-navy">{stats.by_dependency?.[s.key] || 0}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="soem-card p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="pl-9 pr-4 py-2 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
            placeholder="Buscar por titular o dependencia..." aria-label="Buscar autoridad" value={filters.q}
            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} />
        </div>
        <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
          value={filters.dependency_name} aria-label="Filtrar por tipo" onChange={e => setFilters(f => ({ ...f, dependency_name: e.target.value }))}>
          <option value="">Todas las dependencias</option>
          {Object.entries(AGENCY_TYPES).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
        </select>
        <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
          value={filters.jurisdiction_level} aria-label="Filtrar por jurisdicción" onChange={e => setFilters(f => ({ ...f, jurisdiction_level: e.target.value }))}>
          <option value="">Toda jurisdicción</option>
          {Object.entries(JURISDICTION_LEVELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {hasFilters && (
          <button onClick={() => setFilters({ q: '', dependency_name: '', jurisdiction_level: '' })}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-soem-primary">
            <X size={14} /> Limpiar
          </button>
        )}
        {/* View toggle */}
        <div className="flex items-center border border-slate-300 rounded-sm overflow-hidden ml-auto">
          <button onClick={() => setView('grid')}
            className={`p-2 ${viewMode === 'grid' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}
            title="Vista de cuadrícula">
            <LayoutGrid size={14} />
          </button>
          <button onClick={() => setView('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}
            title="Vista de lista">
            <List size={14} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(3)].map((_, i) => <div key={i} className="soem-card h-48 animate-pulse bg-slate-100" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="soem-card p-16 text-center">
          <span className="text-6xl block mb-4">🛡</span>
          <p className="text-slate-500 font-medium">
            {hasFilters ? 'No se encontraron autoridades.' : 'No hay autoridades registradas.'}
          </p>
          {canEdit && !hasFilters && (
            <button onClick={() => navigate('/dashboard/authorities/new')}
              className="soem-btn-primary px-5 py-2 text-sm mt-4 inline-flex items-center gap-2">
              <Plus size={14} /> Crear autoridad
            </button>
          )}
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map(a => (
                <AuthCard key={a.id} auth={a} canEdit={canEdit}
                  onView={id => navigate(`/dashboard/authorities/${id}`)}
                  onEdit={id => navigate(`/dashboard/authorities/${id}/edit`)} />
              ))}
            </div>
          ) : (
            <div className="soem-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="soem-table-header">
                    <th className="px-4 py-3 text-left">Código</th>
                    <th className="px-4 py-3 text-left">Dependencia</th>
                    <th className="px-4 py-3 text-left">Jurisdicción</th>
                    <th className="px-4 py-3 text-left">Titular</th>
                    <th className="px-4 py-3 text-left">Grado</th>
                    <th className="px-4 py-3 text-left">Teléfono</th>
                    <th className="px-4 py-3 text-left">Correo</th>
                    <th className="px-4 py-3 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(a => {
                    const at = AGENCY_TYPES[a.dependency_name] || AGENCY_TYPES.proteccion_civil;
                    const jl = JURISDICTION_LEVELS[a.jurisdiction_level] || { label: a.jurisdiction_level };
                    const rank = RANKS[a.rank];
                    return (
                      <tr key={a.id} className="soem-table-row" data-testid={`auth-row-${a.id}`}>
                        <td className="px-4 py-3 text-xs text-slate-400 font-mono">{a.authority_code}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{at.emoji}</span>
                            <span className="font-semibold text-soem-navy">{at.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full font-semibold border">
                            {jl.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{a.titular_name || '—'}</td>
                        <td className="px-4 py-3">
                          {rank ? (
                            <span className="bg-white border text-slate-600 text-xs px-2 py-0.5 rounded-full font-medium">
                              {rank.label}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{a.direct_phone || '—'}</td>
                        <td className="px-4 py-3 text-slate-500 max-w-32 truncate">{a.institutional_email || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button onClick={() => navigate(`/dashboard/authorities/${a.id}`)}
                              className="text-soem-primary hover:underline text-xs font-semibold">Ver</button>
                            {canEdit && (
                              <button onClick={() => navigate(`/dashboard/authorities/${a.id}/edit`)}
                                className="text-slate-500 hover:underline text-xs">Editar</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
